import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CompanyEnum } from '../../models/company.model';
import { combineLatest, map, Observable } from 'rxjs';
import { AyaSchoolDataByLanguage, JuniorPriceLists, PriceList, YoungAdultsPriceLists } from '../../models';
import { PriceListsDataComponent } from '../price-lists-data/price-lists-data.component';
import { OnlineCoursesComponent } from '../online-courses/online-courses.component';
import { PublicHolidaysComponent } from '../public-holidays/public-holidays.component';
import { TermsAndConditionsComponent } from '../terms-and-conditions/terms-and-conditions.component';
import { TableOfContent } from '../../models/types';
import { COMPANY_LOGO_PATH_PREFIX } from '../../utils/constant';
import { PriceListArea } from '../../models/pricelist-area.model';
import { TableOfContentComponent } from './table-of-content/table-of-content.component';
import { SharedDataStore } from '../../store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe, NgIf } from '@angular/common';
import { JuniorCampsPriceListDataComponent } from '../junior-camps-pricelist-data/junior-camps-price-list-data.component';
import { AyaDataComponent } from '../aya-data/aya-data.component';
import { YoungAdultsPriceListDataComponent } from '../young-adults-pricelist-data/young-adults-price-list-data.component';
import { WhyChoosePositivelyLanguagesComponent } from '../why-choose-positively-languages/why-choose-positively-languages.component';

@Component({
  standalone: true,
  selector: 'lx-price-lists-portal-printable-price-lists',
  imports: [
    PriceListsDataComponent,
    OnlineCoursesComponent,
    PublicHolidaysComponent,
    TermsAndConditionsComponent,
    WhyChoosePositivelyLanguagesComponent,
    TableOfContentComponent,
    AsyncPipe,
    NgIf,
    JuniorCampsPriceListDataComponent,
    AyaDataComponent,
    YoungAdultsPriceListDataComponent,
  ],
  templateUrl: './printable-pricelists.component.html',
  styleUrls: ['./printable-pricelists.component.scss'],
})
export class PrintablePriceListsComponent implements OnInit {
  priceListArea$: Observable<PriceListArea> | undefined;

  kaplanPriceLists$: Observable<PriceList[]> | undefined;
  alpadiaPriceLists$: Observable<PriceList[]> | undefined;
  juniorCampsPriceLists$: Observable<JuniorPriceLists> | undefined;
  youngAdultsPriceLists$: Observable<YoungAdultsPriceLists> | undefined;
  ayaSchoolTables$: Observable<AyaSchoolDataByLanguage[] | undefined>;

  tableOfContents$: Observable<TableOfContent[]> | undefined;

  CompanyEnum = CompanyEnum;
  companyLogoPath = `${COMPANY_LOGO_PATH_PREFIX}${CompanyEnum.KIL}.svg`;

  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.priceListArea$ = this.sharedDataStore.priceListArea$;
    this.kaplanPriceLists$ = this.sharedDataStore.priceListsForCompany(CompanyEnum.KIL);
    this.alpadiaPriceLists$ = this.sharedDataStore.priceListsForCompany(CompanyEnum.ALP);
    this.juniorCampsPriceLists$ = this.sharedDataStore.juniorCampsPriceLists$;
    this.youngAdultsPriceLists$ = this.sharedDataStore.youngAdultsPriceLists$;
    this.ayaSchoolTables$ = this.sharedDataStore.ayaSchoolTables$;

    this.tableOfContents$ = combineLatest([this.kaplanPriceLists$, this.alpadiaPriceLists$, this.juniorCampsPriceLists$, this.youngAdultsPriceLists$]).pipe(
      map(([kaplanPriceList, alpadiaPriceList, juniorPriceList, youngAdultsPriceList]) =>
        this.buildTableOfContents([...kaplanPriceList, ...alpadiaPriceList], juniorPriceList, youngAdultsPriceList),
      ),
      takeUntilDestroyed(this.destroyRef),
    );
  }

  private buildTableOfContents(
    adultPriceLists: PriceList[],
    juniorPriceLists: JuniorPriceLists,
    youngAdultsPriceLists: YoungAdultsPriceLists,
  ): TableOfContent[] {
    const adultEntries = this.mapToTableOfContentEntries(
      adultPriceLists,
      (item) => item.country?.name,
      (item) => item.name,
    );

    const juniorEntries = this.mapToTableOfContentEntries(
      juniorPriceLists?.camps,
      (item) => item.juniorCampInfo.country?.name,
      (item) => item.juniorCampInfo.school?.name,
    );

    const youngAdultsEntries = this.mapToTableOfContentEntries(
      youngAdultsPriceLists?.schools,
      (item) => item.youngAdultSchoolInfo.country?.name,
      (item) => item.youngAdultSchoolInfo.school?.name,
    );

    const allEntries = [...adultEntries, ...juniorEntries, ...youngAdultsEntries];

    const groupedByCountry = allEntries.reduce<Record<string, Set<string>>>((grouped, { country, school }) => {
      if (!grouped[country]) {
        grouped[country] = new Set<string>();
      }
      grouped[country].add(school);
      return grouped;
    }, {});

    return Object.entries(groupedByCountry).map(([title, schoolSet]) => ({
      title,
      items: [...schoolSet],
    }));
  }

  /**
   * Maps a list of items to table of content entries.
   * @param list the list of items to map
   * @param countryPath the function to extract the country name from an item
   * @param schoolPath the function to extract the school name from an item
   * @returns an array of table of content entries
   */
  private mapToTableOfContentEntries<T>(list: T[] | undefined, countryPath: (item: T) => string | undefined, schoolPath: (item: T) => string | undefined) {
    return (list ?? []).map((item) => ({
      country: countryPath(item),
      school: schoolPath(item)?.toLowerCase(),
    }));
  }
}
